import { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GUIDED_TOUR_STEPS } from './guidedTourSteps';
import { markGuidedTourCompleted } from './guidedTourStorage';
import './GuidedTourModal.scss';

export default function GuidedTourModal({ open, onClose, userRole, userId }) {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const visibleSteps = useMemo(() => {
    return GUIDED_TOUR_STEPS.filter((step) => !step.roles || step.roles.includes(userRole));
  }, [userRole]);

  useEffect(() => {
    if (open) {
      setActiveStep(0);
    }
  }, [open]);

  if (!visibleSteps.length) {
    return null;
  }

  const currentStep = visibleSteps[Math.min(activeStep, visibleSteps.length - 1)];
  const Icon = currentStep.icon;
  const isLastStep = activeStep === visibleSteps.length - 1;

  const handleClose = () => {
    markGuidedTourCompleted(userId);
    onClose();
  };

  const handleOpenRoute = () => {
    markGuidedTourCompleted(userId);
    onClose();
    navigate(currentStep.route);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className="guided-tour-dialog"
    >
      <DialogTitle>
        Guided Tour
      </DialogTitle>
      <DialogContent>
        <div className="guided-tour-shell">
          <div className="guided-tour-progress" aria-hidden="true">
            {visibleSteps.map((step, index) => (
              <span
                key={step.key}
                className={`guided-tour-progress-dot${index === activeStep ? ' active' : ''}`}
              />
            ))}
          </div>

          <div className="guided-tour-header">
            <div className="guided-tour-badge">
              <Icon size={30} />
            </div>
            <div className="guided-tour-header-copy">
              <span className="guided-tour-overline">Step {currentStep.symbol}</span>
              <h2 className="guided-tour-title">{currentStep.title}</h2>
              <p className="guided-tour-description">{currentStep.description}</p>
            </div>
          </div>

          <ul className="guided-tour-highlights">
            {currentStep.highlights.map((highlight) => (
              <li key={highlight}>{highlight}</li>
            ))}
          </ul>

          <p className="guided-tour-footer-note">
            You can skip this now, close the popup, or reopen it later from the Guided Tour item in the navbar.
          </p>
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
        <div>
          <Button onClick={handleClose}>Skip tour</Button>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button onClick={() => setActiveStep((step) => Math.max(step - 1, 0))} disabled={activeStep === 0}>
            Back
          </Button>
          <Button variant="outlined" onClick={handleOpenRoute}>
            {currentStep.actionLabel}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (isLastStep) {
                handleClose();
                return;
              }
              setActiveStep((step) => Math.min(step + 1, visibleSteps.length - 1));
            }}
          >
            {isLastStep ? 'Finish' : 'Next'}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}